/**
 * Config for "Mejores Amigos" video (MuxVideoPlayerWithCTA).
 * Used by: BioLinkTemplate modal, /invitacion-mejores-amigos, videoCTAConfig instagram.
 *
 * PRICING: Same price ($149.99) for ALL countries - HARDCODED
 *
 * VIDEO LANGUAGE LOGIC:
 * - Spanish-speaking countries → Spanish video (VITE_MEJORES_AMIGOS_PLAYBACK_ID)
 * - Europe (except ES) → English video (VITE_MEJORES_AMIGOS_EN_PLAYBACK_ID)
 * - US, Middle East → English video
 * - Country-specific override if VITE_MEJORES_AMIGOS_XX_PLAYBACK_ID exists
 */

import { appendBrandReferenceId } from "./stripeReferenceId";

// =============================================================================
// STRIPE PAYMENT LINK - Same for all countries (configurable via Railway env)
// =============================================================================

const MEJORES_AMIGOS_URL_DEFAULT = "";
const MEJORES_AMIGOS_URL_BASE =
  (import.meta.env.VITE_MEJORES_AMIGOS_CTA_URL as string) || MEJORES_AMIGOS_URL_DEFAULT;
const MEJORES_AMIGOS_URL = appendBrandReferenceId(MEJORES_AMIGOS_URL_BASE);

// =============================================================================
// VIDEO PLAYBACK IDs
// =============================================================================

// Helper to get env var (returns empty string if not set or empty)
const getEnvVar = (envVar: string | undefined): string => {
  return envVar && envVar.trim().length > 0 ? envVar.trim() : "";
};

// Main playback ID (Spanish) - VITE_MEJORES_AMIGOS_PLAYBACK_ID
export const MEJORES_AMIGOS_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_MEJORES_AMIGOS_PLAYBACK_ID as string);

// English playback ID - VITE_MEJORES_AMIGOS_EN_PLAYBACK_ID
// Used as fallback for Europe (except Spain), US, Middle East
export const MEJORES_AMIGOS_EN_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_MEJORES_AMIGOS_EN_PLAYBACK_ID as string);

// Export for backwards compatibility
export const MEJORES_AMIGOS_CTA_URL = MEJORES_AMIGOS_URL;

// =============================================================================
// COUNTRY/REGION DEFINITIONS
// =============================================================================

// Spanish-speaking countries (use Spanish video)
const SPANISH_SPEAKING_COUNTRIES = [
  "ES", // Spain
  "MX", "AR", "CO", "PE", "CL", "VE", "EC", "BO", "PY", "UY", // LATAM
  "CR", "PA", "DO", "GT", "HN", "NI", "SV", "CU", "PR", // Central America & Caribbean
];

// European countries (use English video)
const EUROPEAN_COUNTRIES = [
  "DE", "FR", "IT", "PT", "NL", "BE", "AT", "CH", "GB", "IE",
  "SE", "NO", "DK", "FI", "PL", "CZ", "GR", "RO", "HU", "SK",
  "BG", "HR", "SI", "LT", "LV", "EE", "LU", "MT", "CY",
];

// Middle East countries (use English video)
const MIDDLE_EAST_COUNTRIES = ["AE", "SA", "QA", "KW", "BH", "OM"];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a country is Spanish-speaking
 */
function isSpanishSpeakingCountry(countryCode: string): boolean {
  return SPANISH_SPEAKING_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Check if a country is European (except Spain which is Spanish-speaking)
 */
function isEuropeanCountry(countryCode: string): boolean {
  return EUROPEAN_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Check if a country is in Middle East
 */
function isMiddleEastCountry(countryCode: string): boolean {
  return MIDDLE_EAST_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Get playback ID by country without implicit fallback.
 * - Spanish-speaking → Spanish video
 * - Europe, US, Middle East → English video
 */
function getDefaultPlaybackIdForCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();
  
  // Spanish-speaking countries get Spanish video only
  if (isSpanishSpeakingCountry(upperCode)) {
    return MEJORES_AMIGOS_PLAYBACK_ID || "";
  }
  
  // US, Europe, Middle East get English video only
  if (upperCode === "US" || isEuropeanCountry(upperCode) || isMiddleEastCountry(upperCode)) {
    return MEJORES_AMIGOS_EN_PLAYBACK_ID || "";
  }
  
  // Unknown countries: no implicit fallback playback
  return "";
}

// =============================================================================
// CONFIGURATION FUNCTIONS
// =============================================================================

/**
 * Get Mejores Amigos video configuration
 */
export function getMejoresAmigosVideoConfig() {
  return {
    ctaUrls: {
      // Same price for ALL countries
      default: MEJORES_AMIGOS_URL,
    },
  };
}

/**
 * Get CTA URL for a specific country
 * Returns the same URL for all countries ($149.99)
 */
export function getMejoresAmigosCTAUrlByCountry(_countryCode: string): string {
  return MEJORES_AMIGOS_URL;
}

/**
 * Get playback ID for a specific country.
 * 
 * Priority:
 * 1. For ES (Spain): always use default Spanish playback to avoid network issues with ES-specific override.
 * 2. Country-specific env var (VITE_MEJORES_AMIGOS_XX_PLAYBACK_ID) for other countries.
 * 3. Language-based fallback:
 *    - Spanish-speaking → VITE_MEJORES_AMIGOS_PLAYBACK_ID (Spanish)
 *    - Europe/US/Middle East → VITE_MEJORES_AMIGOS_EN_PLAYBACK_ID (English)
 */
export function getMejoresAmigosPlaybackIdByCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();

  // Spain: skip country override to avoid network/playback issues; use main Spanish video
  if (upperCode === "ES") {
    return getDefaultPlaybackIdForCountry(upperCode);
  }

  // Check for country-specific env var
  const countryEnvVar = getEnvVar(
    import.meta.env[`VITE_MEJORES_AMIGOS_${upperCode}_PLAYBACK_ID`] as string
  );

  if (countryEnvVar) {
    return countryEnvVar;
  }

  // Fallback based on language/region
  return getDefaultPlaybackIdForCountry(upperCode);
}

/**
 * Get playback ID directly by language code.
 * @param language - Language code like "es", "en", "es-AR", "en-US"
 */
export function getMejoresAmigosPlaybackIdByLanguage(language: string): string {
  const baseLang = language.split("-")[0].toLowerCase();
  
  if (baseLang === "es") {
    return MEJORES_AMIGOS_PLAYBACK_ID;
  }
  
  // English and all other languages
  return MEJORES_AMIGOS_EN_PLAYBACK_ID;
}
